import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../config/firebaseConfig';
import { HiOutlineCheckCircle, HiOutlineExclamationTriangle, HiOutlineMapPin, HiOutlineUser, HiOutlinePhone, HiOutlineCalendarDays, HiOutlineUserGroup, HiOutlineClock, HiOutlineCurrencyEuro, HiOutlineArrowLeft, HiOutlineWallet } from 'react-icons/hi2';
import { MdOutlineExplore } from 'react-icons/md';
import './ExperienceBookingConfirmation.css';
import { getAuth } from 'firebase/auth';

const ExperienceBookingConfirmation = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { orderId } = useParams();
    const searchParams = new URLSearchParams(location.search);
    const sessionId = searchParams.get('session_id');
    const status = searchParams.get('status');
    
    const [bookingData, setBookingData] = useState(location.state?.bookingData || null);
    const [paymentStatus, setPaymentStatus] = useState(status || '');
    const [experienceData, setExperienceData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const auth = getAuth();

    // Fetch booking data from the orderId in the URL if not already available
    useEffect(() => {
        const fetchBookingData = async () => {
            if (orderId && !bookingData) {
                try {
                    // Try to find the booking by orderId in experienceBookings collection
                    const bookingsRef = collection(db, 'experienceBookings');
                    const q = query(bookingsRef, where('orderId', '==', orderId));
                    const querySnapshot = await getDocs(q);
                    
                    if (!querySnapshot.empty) {
                        const bookingDoc = querySnapshot.docs[0];
                        const fetchedBookingData = { id: bookingDoc.id, ...bookingDoc.data() };
                        setBookingData(fetchedBookingData);
                    } else {
                        setError(`Booking with ID ${orderId} not found`);
                    }
                } catch (err) {
                    console.error('Error fetching booking data:', err);
                    setError('Failed to load booking information');
                }
            }
        };
        
        fetchBookingData();
    }, [orderId, bookingData]);
    
    // Fetch payment status if session ID is available
    useEffect(() => {
        const fetchPaymentStatus = async () => {
            if (sessionId && sessionId.length > 0) {
                try {
                    const response = await fetch(`https://bosp-yachts-backend.vercel.app/api/bookings/payment-status/${sessionId}`, {
                        headers: {
                            'Authorization': `Bearer ${await auth.currentUser.getIdToken()}`
                        }
                    });
                    
                    if (response.ok) {
                        const data = await response.json();
                        setPaymentStatus(data.paymentStatus);
                        
                        // If we don't have booking data yet, use the one from the payment status
                        if (!bookingData && data.bookingId) {
                            const bookingDoc = await getDoc(doc(db, 'experienceBookings', data.bookingId));
                            if (bookingDoc.exists()) {
                                setBookingData({ id: bookingDoc.id, ...bookingDoc.data() });
                            }
                        }
                    } else {
                        console.error('Failed to fetch payment status');
                    }
                } catch (err) {
                    console.error('Error checking payment status:', err);
                }
            }
        };
        
        fetchPaymentStatus();
    }, [sessionId, bookingData]);

    // Fetch experience data once we have booking data
    useEffect(() => {
        const fetchExperienceData = async () => {
            if (!bookingData?.experienceId) {
                // No experienceId — booking data from state is enough
                if (bookingData) setLoading(false);
                return;
            }

            try {
                const experienceRef = doc(db, 'experiences', bookingData.experienceId);
                const experienceSnap = await getDoc(experienceRef);

                if (experienceSnap.exists()) {
                    setExperienceData(experienceSnap.data());
                }
            } catch (err) {
                console.error('Error fetching experience data:', err);
            } finally {
                if (bookingData) {
                    setLoading(false);
                }
            }
        };

        if (bookingData) {
            fetchExperienceData();
        }
    }, [bookingData?.experienceId]);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);
    
    // Set loading to false if we have all data or if there's an error
    useEffect(() => {
        if (bookingData || error) {
            setLoading(false);
        }
    }, [bookingData, experienceData, error]);

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    if (loading) {
        return (
            <div className="ebc-page">
                <div className="ebc-loading">
                    <div className="spinner"></div>
                    <p>Loading booking confirmation...</p>
                </div>
            </div>
        );
    }

    if (error || !bookingData) {
        return (
            <div className="ebc-page">
                <div className="ebc-wrapper">
                    <div className="ebc-card">
                        <div className="ebc-header error">
                            <HiOutlineExclamationTriangle />
                            <h1>Error</h1>
                        </div>
                        <div className="ebc-body">
                            <p className="ebc-error-text">{error || 'Booking information not found.'}</p>
                            <button className="ebc-btn primary" onClick={() => navigate('/')}>
                                <HiOutlineArrowLeft />
                                Return to Home
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="ebc-page">
            <div className="ebc-wrapper">
                <div className="ebc-card">
                    {/* Header */}
                    <div className={`ebc-header ${status === 'canceled' ? 'error' : 'success'}`}>
                        {status === 'canceled' ? (
                            <>
                                <HiOutlineExclamationTriangle />
                                <h1>Booking Incomplete</h1>
                            </>
                        ) : (
                            <>
                                <HiOutlineCheckCircle />
                                <h1>{(paymentStatus === 'paid' || status === 'success') ? 'Booking Confirmed!' : 'Booking Created'}</h1>
                            </>
                        )}
                    </div>

                    <div className="ebc-body">
                        {/* Status Message */}
                        <div className={`ebc-status-banner ${status === 'canceled' ? 'error' : 'success'}`}>
                            {status === 'canceled' ? (
                                <p>Your payment was not completed. You can try booking again or contact customer support.</p>
                            ) : (paymentStatus === 'paid' || status === 'success') ? (
                                <p>Your payment was successful and your experience booking has been confirmed.</p>
                            ) : (
                                <p>Your experience booking has been created. Please check your email for confirmation details.</p>
                            )}
                        </div>

                        {status !== 'canceled' && (
                            <>
                                {/* Experience Details */}
                                <div className="ebc-section">
                                    <h3 className="ebc-section-title">
                                        <MdOutlineExplore />
                                        Experience Details
                                    </h3>
                                    <div className="ebc-experience-header">
                                        {bookingData.experienceImage && (
                                            <img
                                                src={bookingData.experienceImage}
                                                alt={bookingData.experienceName}
                                                className="ebc-thumb"
                                            />
                                        )}
                                        <div className="ebc-experience-info">
                                            <div className="ebc-row">
                                                <span className="ebc-row-label">Experience</span>
                                                <span className="ebc-row-value">{bookingData.experienceName}</span>
                                            </div>
                                            <div className="ebc-row">
                                                <span className="ebc-row-label">Option</span>
                                                <span className="ebc-row-value">{bookingData.optionName} ({bookingData.optionType})</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Host Information */}
                                <div className="ebc-section">
                                    <h3 className="ebc-section-title">
                                        <HiOutlineUser />
                                        Host Information
                                    </h3>
                                    <div className="ebc-row">
                                        <span className="ebc-row-label">Host Name</span>
                                        <span className="ebc-row-value">{bookingData.hostName}</span>
                                    </div>
                                    {bookingData.hostPhone && (
                                        <div className="ebc-row">
                                            <span className="ebc-row-label">Host Phone</span>
                                            <span className="ebc-row-value">+{bookingData.hostPhone}</span>
                                        </div>
                                    )}
                                </div>

                                {/* Booking Details */}
                                <div className="ebc-section">
                                    <h3 className="ebc-section-title">
                                        <HiOutlineCalendarDays />
                                        Booking Details
                                    </h3>
                                    <div className="ebc-row">
                                        <span className="ebc-row-label">Date</span>
                                        <span className="ebc-row-value">{formatDate(bookingData.date)}</span>
                                    </div>
                                    <div className="ebc-row">
                                        <span className="ebc-row-label">Start Time</span>
                                        <span className="ebc-row-value">{bookingData.startTime}</span>
                                    </div>
                                    <div className="ebc-row">
                                        <span className="ebc-row-label">Duration</span>
                                        <span className="ebc-row-value">{bookingData.duration}h{bookingData.durationMinutes ? ` ${bookingData.durationMinutes}m` : ''}</span>
                                    </div>
                                    <div className="ebc-row">
                                        <span className="ebc-row-label">Guests</span>
                                        <span className="ebc-row-value">{bookingData.guestCount} {bookingData.guestCount === 1 ? 'person' : 'people'}</span>
                                    </div>
                                </div>

                                {/* Meeting Point */}
                                {bookingData.meetingPoint && (
                                    <div className="ebc-section">
                                        <h3 className="ebc-section-title">
                                            <HiOutlineMapPin />
                                            Meeting Point
                                        </h3>
                                        <div className="ebc-row">
                                            <span className="ebc-row-label">Location</span>
                                            <span className="ebc-row-value">{bookingData.meetingPoint}</span>
                                        </div>
                                        <div className="ebc-row">
                                            <span className="ebc-row-label">Description</span>
                                            <span className="ebc-row-value">{bookingData.meetingPointDescription}</span>
                                        </div>
                                        {bookingData.googleMapsLink && (
                                            <div className="ebc-row">
                                                <span className="ebc-row-label">Directions</span>
                                                <span className="ebc-row-value">
                                                    <a href={bookingData.googleMapsLink} target="_blank" rel="noopener noreferrer" className="ebc-link">
                                                        View on Google Maps
                                                    </a>
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Payment Summary */}
                                {bookingData.priceBreakdown && (
                                    <div className="ebc-section">
                                        <h3 className="ebc-section-title">
                                            <HiOutlineCurrencyEuro />
                                            Payment Summary
                                        </h3>
                                        <div className="ebc-row">
                                            <span className="ebc-row-label">Base Price</span>
                                            <span className="ebc-row-value">€{bookingData.priceBreakdown.basePrice?.toFixed(2)}</span>
                                        </div>
                                        {bookingData.priceBreakdown.discount > 0 && (
                                            <div className="ebc-row">
                                                <span className="ebc-row-label">Affiliate Discount</span>
                                                <span className="ebc-row-value ebc-savings">{bookingData.priceBreakdown.discount}% off</span>
                                            </div>
                                        )}
                                        {bookingData.priceBreakdown.balanceUsed > 0 && (
                                            <div className="ebc-row">
                                                <span className="ebc-row-label"><HiOutlineWallet style={{ width: 16, height: 16, marginRight: 6, verticalAlign: 'middle' }} />Balance Used</span>
                                                <span className="ebc-row-value ebc-savings">-€{bookingData.priceBreakdown.balanceUsed?.toFixed(2)}</span>
                                            </div>
                                        )}
                                        <div className="ebc-row ebc-total">
                                            <span className="ebc-row-label">Total Paid</span>
                                            <span className="ebc-row-value">
                                                {bookingData.priceBreakdown.amountCharged === 0
                                                    ? 'Paid with Balance'
                                                    : `€${(bookingData.priceBreakdown.amountCharged ?? bookingData.priceBreakdown.finalPrice)?.toFixed(2)}`}
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}

                        {/* Actions */}
                        <div className="ebc-actions">
                            <button className="ebc-btn primary" onClick={() => navigate('/my-bookings')}>
                                View My Bookings
                            </button>
                            <button className="ebc-btn secondary" onClick={() => navigate('/experiences')}>
                                Browse More Experiences
                            </button>
                            <button className="ebc-btn ghost" onClick={() => navigate('/')}>
                                <HiOutlineArrowLeft />
                                Return to Home
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ExperienceBookingConfirmation;